import { trimDecimals } from '@einhaus/meteoric-helpers';
import { calculateRiskCapitalStats } from './calculateRiskCapitalStats.js';
import { checkTradeGroupForCompletion } from './checkTradeGroupForCompletion.js';
import { getTradeTotalCostSigned } from './getTradeTotalCostSigned.js';
const getSignedRecordedTransactionValue = (type, value) => {
    if (type === 'dividend' || type === 'dividendReinvestment')
        return Math.abs(value);
    return getTradeTotalCostSigned(Math.abs(value), type);
};
/**
 * Calculates presentation metrics from the fills recorded for a completed live portfolio trade.
 * This is deliberately separate from historical backtest evaluation, whose canonical economics
 * are produced by the historical execution engine and persisted on the trade.
 */
export const calculateUserPortfolioTradePerformance = (trade) => {
    if (!trade.transactions.length)
        return null;
    const transactions = trade.transactions.slice().sort((first, second) => first.timestamp - second.timestamp);
    if (!checkTradeGroupForCompletion(transactions))
        return null;
    const openingTransactions = transactions.filter((transaction) => transaction.isOpeningTrade);
    const firstOpeningTransaction = openingTransactions[0];
    if (!firstOpeningTransaction)
        return null;
    let grossCashFlow = 0;
    let fees = 0;
    let openingValue = 0;
    let closingValue = 0;
    let openingRiskCapital = 0;
    let startingBalance = 0;
    for (const transaction of transactions) {
        if (!Number.isFinite(transaction.value) ||
            !Number.isFinite(transaction.fees) ||
            transaction.fees < 0 ||
            !Number.isFinite(transaction.riskCapital) ||
            transaction.riskCapital < 0) {
            throw new Error(`Portfolio trade ${trade.id} contains invalid recorded transaction economics`);
        }
        grossCashFlow += getSignedRecordedTransactionValue(transaction.type, transaction.value);
        fees += transaction.fees;
        if (transaction.isOpeningTrade) {
            openingValue += Math.abs(transaction.value);
            if (Number.isFinite(transaction.riskCapital) && transaction.riskCapital > 0) {
                openingRiskCapital += transaction.riskCapital;
            }
            if (transaction.type !== 'sellToOpen' && transaction.type !== 'assignmentBuy') {
                startingBalance += Math.abs(transaction.value);
            }
        }
        else {
            closingValue += Math.abs(transaction.value);
        }
    }
    const profit = trimDecimals(grossCashFlow - fees);
    const profitBasis = firstOpeningTransaction.type === 'sellToOpen' ? openingRiskCapital : openingValue;
    if (profitBasis <= 0)
        return null;
    const profitPercentage = trimDecimals((profit / profitBasis) * 100);
    const effectiveRiskCapital = openingRiskCapital > 0 ? openingRiskCapital : openingValue;
    const endTimestamp = transactions.at(-1)?.timestamp ?? firstOpeningTransaction.timestamp;
    const { averageRiskCapitalPerDay, returnOnRiskCapital } = calculateRiskCapitalStats([
        {
            id: String(trade.id),
            start: firstOpeningTransaction.timestamp,
            end: endTimestamp,
            direction: firstOpeningTransaction.direction,
            openingTxnType: firstOpeningTransaction.type,
            entityId: firstOpeningTransaction.entityId,
            entityType: firstOpeningTransaction.entityType,
            riskCapital: effectiveRiskCapital,
            profit,
            openingValue,
            closingValue
        }
    ]);
    startingBalance = trimDecimals(startingBalance);
    return {
        averageRiskCapitalPerDay,
        endingBalance: trimDecimals(startingBalance + profit),
        endingValue: firstOpeningTransaction.type === 'sellToOpen' ? profit : trimDecimals(closingValue),
        fees: trimDecimals(fees),
        openingValue: trimDecimals(openingValue),
        profit,
        profitPercentage,
        returnOnRiskCapital,
        startingBalance
    };
};
//# sourceMappingURL=calculateUserPortfolioTradePerformance.js.map